import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo
} from 'react';
import { x, styled } from '@xstyled/styled-components';
import { t } from 'ttag';
// NOTE: We intentionally do not import the Item-based Text component for inline modal editing,
// because Item classes aren't usable directly as JSX. Implement a lightweight inline editable instead.
import { useEditorStore } from '~/shell/Store';
import CloseIcon from '~/assets/icons/close.svg';
import TrashIcon from '~/assets/icons/trashIcon.svg';
// Inline SVG components so we can dynamically change fill color when selected
const CheckSvg: React.FC<{
  color?: string;
  width?: number;
  height?: number;
}> = ({ color = '#9738FF', width = 14.63, height = 10.56 }) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 16 11"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden={true}
    style={{ display: 'block' }}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.5625 8.91345L13.9255 0.550476C14.2428 0.233175 14.7572 0.233175 15.0745 0.550476C15.3918 0.867777 15.3918 1.38222 15.0745 1.69952L6.13702 10.637C5.81972 10.9543 5.30528 10.9543 4.98798 10.637L0.925476 6.57453C0.608175 6.25723 0.608175 5.74278 0.925476 5.42548C1.24278 5.10818 1.75722 5.10818 2.07452 5.42548L5.5625 8.91345Z"
      fill={color}
    />
  </svg>
);

const XSvg: React.FC<{ color?: string; width?: number; height?: number }> = ({
  color = '#9738FF',
  width = 11.38,
  height = 11.3
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 13 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden={true}
    style={{ display: 'block' }}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0.975769 1.69952C0.658468 1.38222 0.658468 0.867777 0.975769 0.550476C1.29307 0.233175 1.80752 0.233175 2.12482 0.550476L11.8748 10.3005C12.1921 10.6178 12.1921 11.1322 11.8748 11.4495C11.5575 11.7668 11.0431 11.7668 10.7258 11.4495L0.975769 1.69952Z"
      fill={color}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.8748 1.69952C12.1921 1.38222 12.1921 0.867777 11.8748 0.550476C11.5575 0.233175 11.0431 0.233175 10.7258 0.550476L0.975768 10.3005C0.658467 10.6178 0.658467 11.1322 0.975768 11.4495C1.29307 11.7668 1.80752 11.7668 2.12482 11.4495L11.8748 1.69952Z"
      fill={color}
    />
  </svg>
);

export interface AddCardsProps {
  open: boolean;
  onClose: () => void;
  items?: Array<{
    id: string | number;
    value: string;
    correct?: boolean;
  }>;
}

interface InlineEditableProps {
  value: string;
  editable: boolean;
  onChange: (val: string) => void;
  inlineId?: string | number;
}

const StyledTextarea = styled.textarea`
  width: 100%;
  border: none;
  background: transparent;
  outline: none;
  resize: none;
  overflow: hidden;
  font-family: inherit;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  text-align: left;
  padding: 2px 0;
  min-height: 20px;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
  cursor: text;

  &:disabled {
    cursor: default;
    opacity: 1;
    color: inherit;
  }

  &::placeholder {
    color: #9aa0a6;
    font-weight: 400;
  }

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

const autoResizeTextarea = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

/** Auto-expanding multi-line field for Quiz5 Add Cards modal (CAT-499). */
const InlineEditable: React.FC<InlineEditableProps> = ({
  value,
  editable,
  onChange,
  inlineId
}) => {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    autoResizeTextarea(ref.current);
  }, [value]);

  return (
    <StyledTextarea
      ref={ref}
      rows={1}
      disabled={!editable}
      value={value || ''}
      data-inline-id={inlineId}
      placeholder={t`Enter Card Title`}
      onChange={e => {
        onChange(e.target.value);
        autoResizeTextarea(e.currentTarget);
      }}
    />
  );
};

export const AddCards: React.FC<AddCardsProps> = ({
  open,
  onClose,
  items = [],
  children
}) => {
  const MAX_CARDS = 8;
  const interpretCorrect = (val: unknown): 'left' | 'right' | null => {
    if (val === undefined || val === null) return null;
    if (val === true || val === 'true') return 'left'; // left square = correct true
    if (val === false || val === 'false') return 'right';
    return null;
  };
  const {
    updateSequence,
    contentEditable,
    removeSubItem,
    addSubItem,
    sequence
  } = useEditorStore();
  const [localValues, setLocalValues] = useState<
    Record<string | number, string>
  >(() =>
    items.reduce<Record<string | number, string>>((acc, it) => {
      acc[it.id] = it.value;
      return acc;
    }, {})
  );
  const originalValuesRef = useRef<Record<string | number, string>>({
    ...localValues
  });
  const submittedRef = useRef(false);
  const [selectionMap, setSelectionMap] = useState<
    Record<string | number, 'left' | 'right' | null>
  >(() => {
    const init: Record<string | number, 'left' | 'right' | null> = {};
    items.forEach(i => {
      init[i.id] = interpretCorrect(i.correct);
    });
    return init;
  });
  const originalSelectionRef = useRef<
    Record<string | number, 'left' | 'right' | null>
  >({ ...selectionMap });
  const lastSubmittedSelectionRef = useRef<
    Record<string | number, 'left' | 'right' | null>
  >({});
  const [pendingDelete, setPendingDelete] = useState<Set<string | number>>(
    new Set()
  );
  const [pendingNew, setPendingNew] = useState<Array<string | number>>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Sync newly added items (avoid triggering state updates every render when items prop identity changes)
  useEffect(() => {
    if (!items || items.length === 0) return;
    setLocalValues(prev => {
      let changed = false;
      const updated = { ...prev };
      for (const i of items) {
        if (updated[i.id] === undefined) {
          updated[i.id] = i.value;
          originalValuesRef.current[i.id] = i.value;
          changed = true;
        }
      }
      return changed ? updated : prev;
    });
    setSelectionMap(prev => {
      let changed = false;
      const updated = { ...prev };
      for (const i of items) {
        if (updated[i.id] === undefined) {
          const sel = interpretCorrect(i.correct);
          updated[i.id] = sel;
          originalSelectionRef.current[i.id] = sel;
          changed = true;
        }
      }
      return changed ? updated : prev;
    });
  }, [items]);

  const escHandler = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;
    window.addEventListener('keydown', escHandler);
    return () => window.removeEventListener('keydown', escHandler);
  }, [open, escHandler]);

  // Track open/close transitions to reset unsaved changes on cancel
  const wasOpenRef = useRef(false);
  const itemsSignature = useMemo(
    () => (items ? items.map(i => i.id).join('|') : ''),
    [items]
  );
  const lastItemsSignatureRef = useRef<string>('');
  useEffect(() => {
    // Opening the modal
    if (open) {
      const signatureChanged = lastItemsSignatureRef.current !== itemsSignature;
      if (!wasOpenRef.current || signatureChanged) {
        submittedRef.current = false;
        const baseline: Record<string | number, string> = {};
        items.forEach(i => {
          baseline[i.id] = i.value;
        });
        originalValuesRef.current = baseline;
        wasOpenRef.current = true;
        const selBaseline: Record<string | number, 'left' | 'right' | null> =
          {};
        items.forEach(i => {
          const fromItems = interpretCorrect(i.correct);
          const last = lastSubmittedSelectionRef.current[i.id];
          selBaseline[i.id] = last != null ? last : fromItems;
        });
        originalSelectionRef.current = selBaseline;
        setSelectionMap(selBaseline);
        setPendingDelete(new Set());
        setPendingNew([]);
        lastItemsSignatureRef.current = itemsSignature;
      }
    } else if (wasOpenRef.current && !open) {
      // Closing without submit: revert changes
      if (!submittedRef.current) {
        setLocalValues({ ...originalValuesRef.current });
        setSelectionMap({ ...originalSelectionRef.current });
        setPendingDelete(new Set());
        setPendingNew([]);
        setLocalValues(prev => {
          const cleaned: Record<string | number, string> = {};
          Object.keys(prev).forEach(k => {
            if (typeof k === 'string' && k.startsWith('temp-')) return;
            cleaned[k] = prev[k];
          });
          return cleaned;
        });
      }
      wasOpenRef.current = false;
    }
  }, [open, itemsSignature, items]);

  // Handlers
  const handleToggleSelection = useCallback(
    (id: string | number, pos: 'left' | 'right') => {
      setSelectionMap(m => ({
        ...m,
        [id]: pos // no deselect allowed per requirements
      }));
    },
    []
  );

  const handleMarkDelete = useCallback(
    (id: string | number) => {
      if (!contentEditable) return;
      setPendingDelete(prev => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
    },
    [contentEditable]
  );

  const handleAddCard = useCallback(() => {
    if (!contentEditable) return;
    // Recompute counts with latest state (avoid stale closure bug)
    setPendingNew(prevNew => {
      const existingVisible = items.filter(i => !pendingDelete.has(i.id));
      const newVisible = prevNew.filter(id => !pendingDelete.has(id));
      if (existingVisible.length + newVisible.length >= MAX_CARDS)
        return prevNew; // no change
      const tempId = `temp-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`;
      setLocalValues(v => ({ ...v, [tempId]: '' }));
      setSelectionMap(m => ({ ...m, [tempId]: 'left' }));
      // Focus after DOM paint
      requestAnimationFrame(() => {
        const el = document.querySelector(
          `[data-inline-id="${tempId}"]`
        ) as HTMLElement | null;
        el?.focus();
      });
      return [...prevNew, tempId];
    });
  }, [contentEditable, items, pendingDelete]);

  const handleSubmit = useCallback(async () => {
    if (!contentEditable || submitting) return;
    setSubmitError(null);
    setSubmitting(true);

    const esc = (s: string) =>
      s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    // Snapshot intentions BEFORE local-state mutations so we can build the
    // promise list deterministically.
    const updates: Array<{ id: string | number; sanitized: string }> = [];
    Object.keys(localValues).forEach(idKey => {
      if (pendingDelete.has(idKey)) return;
      if (typeof idKey === 'string' && idKey.startsWith('temp-')) return;
      const currentRaw = localValues[idKey] || '';
      const currentTrimmed = currentRaw.trim();
      const orig = (originalValuesRef.current[idKey] || '').trim();
      if (currentTrimmed === '') return;
      if (currentTrimmed !== orig) {
        // Plain text from the auto-expanding textarea (CAT-499).
        updates.push({ id: idKey, sanitized: esc(currentTrimmed) });
      }
    });

    const selectionUpdates: Array<{ id: string | number; correct: boolean }> =
      [];
    Object.keys(selectionMap).forEach(idKey => {
      if (pendingDelete.has(idKey)) return;
      if (typeof idKey === 'string' && idKey.startsWith('temp-')) return;
      const sel = selectionMap[idKey];
      const origSel = originalSelectionRef.current[idKey];
      if (sel && sel !== origSel) {
        selectionUpdates.push({ id: idKey, correct: sel === 'left' });
      }
    });

    const deletes = Array.from(pendingDelete).filter(
      id => !(typeof id === 'string' && id.startsWith('temp-'))
    );

    const adds: Array<{
      newId: string | number;
      sanitized: string;
      correct: boolean;
    }> = [];
    pendingNew.forEach(newId => {
      if (pendingDelete.has(newId)) return;
      const raw = localValues[newId] || '';
      const trimmed = raw.trim();
      if (!trimmed) return;
      adds.push({
        newId,
        sanitized: esc(trimmed),
        correct: selectionMap[newId] === 'left'
      });
    });

    // Optimistic local state for text edits so the modal shows the sanitized
    // value while the requests are flying.
    if (updates.length) {
      setLocalValues(v => {
        const next = { ...v };
        for (const u of updates) {
          originalValuesRef.current[u.id] = u.sanitized;
          next[u.id] = u.sanitized;
        }
        return next;
      });
    }
    if (selectionUpdates.length) {
      for (const s of selectionUpdates) {
        originalSelectionRef.current[s.id] = s.correct ? 'left' : 'right';
      }
    }

    let questionItemId: string | number | null = null;
    let basePosition = 0;
    if (sequence) {
      const questionItem = (
        Array.isArray(sequence.items)
          ? sequence.items.find(
              (it: { __typename?: string; items?: unknown[] }) =>
                it.__typename === 'Question'
            )
          : null
      ) as { id?: string | number; items?: unknown[] } | null | undefined;
      questionItemId = questionItem?.id ?? null;
      basePosition = questionItem?.items?.length || 0;
    }

    // Store actions never reject (fire-and-forget contract); they resolve
    // with `{ error: true, message }` on failure. We unwrap that into a real
    // throw so the existing try/catch below can keep the modal open and
    // surface the error instead of silently closing on server failures.
    const ensureOk = (result: unknown, fallback: string) => {
      const r = result as { error?: boolean; message?: string } | undefined;
      if (r && r.error) {
        throw new Error(r.message || fallback);
      }
    };

    try {
      // 1) Field updates (title / correct) — safe in parallel, they don't
      //    touch position so backend reorder locking can't race them.
      const updatePromises: Promise<unknown>[] = [];
      for (const u of updates) {
        updatePromises.push(
          updateSequence(u.id, 'title', u.sanitized, 'QUESTION_FIELD')
        );
      }
      for (const s of selectionUpdates) {
        updatePromises.push(updateSequence(s.id, 'correct', s.correct));
      }
      const updateResults = await Promise.all(updatePromises);
      for (const r of updateResults) {
        ensureOk(r, 'Failed to update card.');
      }

      // 2) Deletes — STRICTLY sequential. The Rails subitems#destroy locks the
      //    sequence to reorder remaining positions; sending several DELETEs in
      //    parallel makes follow-ups complete with 200 OK while the actual
      //    delete is silently dropped (the row is already "moved" by the first
      //    transaction's reorder). One delete at a time, await its 200 before
      //    firing the next.
      for (const id of deletes) {
        const res = await removeSubItem(id as string | number);
        ensureOk(res, 'Failed to delete card.');
      }

      // 3) Adds — also sequential, for the same reorder/position reason and to
      //    keep the resulting `position` values monotonic on the server.
      if (sequence) {
        for (const a of adds) {
          basePosition += 1;
          const positionForServer = basePosition;
          const res = await addSubItem({
            sequenceId: sequence.id,
            data: {
              type: 'QUESTION_FIELD',
              value: a.sanitized,
              title: a.sanitized,
              correct: a.correct,
              position: positionForServer,
              parent_id: questionItemId
            },
            newItem: {
              id: a.newId,
              position: positionForServer,
              value: a.sanitized,
              title: a.sanitized,
              correct: a.correct
            }
          });
          ensureOk(res, 'Failed to add card.');
        }
      }

      submittedRef.current = true;
      lastSubmittedSelectionRef.current = { ...selectionMap };
      setSubmitting(false);
      onClose();
    } catch (e) {
      // One or more API calls failed — keep the modal open so the user can
      // retry. Local state already reflects what the user did, so retrying
      // Submit will re-issue only the requests that didn't make it through
      // (optimistic refs were updated above). For now we just surface the
      // error; full per-item retry tracking can come later.
      console.warn('AddCards.handleSubmit failed', e);
      setSubmitError(
        e instanceof Error ? e.message : 'Failed to save changes. Try again.'
      );
      setSubmitting(false);
    }
  }, [
    contentEditable,
    submitting,
    localValues,
    pendingDelete,
    selectionMap,
    pendingNew,
    sequence,
    updateSequence,
    removeSubItem,
    addSubItem,
    onClose
  ]);

  if (!open) return null;

  return (
    <AddCardsOverlay onRequestClose={onClose}>
      <x.div
        w="462px"
        backgroundColor="white"
        borderRadius="20px"
        boxShadow="0 4px 24px rgba(0,0,0,0.15)"
        display="flex"
        flexDirection="column"
        p="30px"
        fontFamily="Raleway, sans-serif"
        onClick={e => e.stopPropagation()}
        position="relative"
        style={{
          minHeight: '489px',
          maxHeight: '85vh',
          overflow: 'hidden'
        }}
      >
        <x.button
          type="button"
          aria-label={t`Close`}
          onClick={onClose}
          position="absolute"
          top="10px"
          right="10px"
          w="32px"
          h="32px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          backgroundColor="transparent"
          border="none"
          cursor="pointer"
          p={0}
          m={0}
        >
          <CloseIcon width={18} height={18} />
        </x.button>
        {children || (
          <>
            <x.div textAlign="left" fontSize="16px" fontWeight={700}>
              {t`Add Cards`}
            </x.div>
            <x.div
              mt="20px"
              textAlign="left"
              fontSize="15.78px"
              fontWeight={400}
            >
              {t`Select the right answer(s)`}
            </x.div>
            <ScrollArea>
              {items
                .filter(item => !pendingDelete.has(item.id))
                .map(item => (
                  <x.div
                    key={item.id}
                    minH="42px"
                    display="flex"
                    alignItems="flex-start"
                    gap="8px"
                    flex="0 0 auto"
                  >
                    <x.div
                      w="272px"
                      minH="42px"
                      backgroundColor="#F5F5F5"
                      borderRadius="5px"
                      px="12px"
                      py="10px"
                      style={{ height: 'auto', boxSizing: 'border-box' }}
                    >
                      <InlineEditable
                        editable={!!contentEditable}
                        value={localValues[item.id]}
                        inlineId={item.id}
                        onChange={(val: string) =>
                          setLocalValues(v => ({ ...v, [item.id]: val }))
                        }
                      />
                    </x.div>
                    <x.div
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      minH="42px"
                      w="76px"
                      flex="0 0 auto"
                    >
                      {(['left', 'right'] as const).map(pos => {
                        const selected = selectionMap[item.id] === pos;
                        const isLeft = pos === 'left';
                        const fill = selected ? '#FFFFFF' : '#9738FF';
                        return (
                          <x.button
                            key={pos}
                            type="button"
                            w="38px"
                            h="38px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            backgroundColor={selected ? '#9738FF' : '#F8F1FF'}
                            style={{
                              border: 'none',
                              padding: 0,
                              margin: 0,
                              cursor: 'pointer',
                              borderRadius: isLeft
                                ? '8px 0 0 8px'
                                : '0 8px 8px 0'
                            }}
                            aria-pressed={selected}
                            onClick={() => handleToggleSelection(item.id, pos)}
                          >
                            {isLeft ? (
                              <CheckSvg color={fill} />
                            ) : (
                              <XSvg color={fill} />
                            )}
                          </x.button>
                        );
                      })}
                    </x.div>
                    {/* Item 3: Delete (trash) button */}
                    <x.button
                      type="button"
                      w="38px"
                      h="38px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      backgroundColor="#F8F1FF"
                      style={{
                        border: 'none',
                        padding: 0,
                        margin: 0,
                        cursor: contentEditable ? 'pointer' : 'not-allowed',
                        borderRadius: '8px'
                      }}
                      disabled={!contentEditable}
                      aria-label={t`Delete card`}
                      onClick={() => handleMarkDelete(item.id)}
                    >
                      <TrashIcon width={38} height={38} aria-hidden={true} />
                    </x.button>
                  </x.div>
                ))}
              {pendingNew
                .filter(id => !pendingDelete.has(id))
                .map(id => (
                  <x.div
                    key={id}
                    minH="42px"
                    display="flex"
                    alignItems="flex-start"
                    gap="8px"
                    flex="0 0 auto"
                  >
                    <x.div
                      w="272px"
                      minH="42px"
                      backgroundColor="#F5F5F5"
                      borderRadius="5px"
                      px="12px"
                      py="10px"
                      style={{ height: 'auto', boxSizing: 'border-box' }}
                    >
                      <InlineEditable
                        editable={!!contentEditable}
                        value={localValues[id] || ''}
                        inlineId={id}
                        onChange={(val: string) =>
                          setLocalValues(v => ({ ...v, [id]: val }))
                        }
                      />
                    </x.div>
                    <x.div
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      minH="42px"
                      w="76px"
                      flex="0 0 auto"
                    >
                      {(['left', 'right'] as const).map(pos => {
                        const selected = selectionMap[id] === pos;
                        const isLeft = pos === 'left';
                        const fill = selected ? '#FFFFFF' : '#9738FF';
                        return (
                          <x.button
                            key={pos}
                            type="button"
                            w="38px"
                            h="38px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            backgroundColor={selected ? '#9738FF' : '#F8F1FF'}
                            style={{
                              border: 'none',
                              padding: 0,
                              margin: 0,
                              cursor: 'pointer',
                              borderRadius: isLeft
                                ? '8px 0 0 8px'
                                : '0 8px 8px 0'
                            }}
                            aria-pressed={selected}
                            onClick={() => handleToggleSelection(id, pos)}
                          >
                            {isLeft ? (
                              <CheckSvg color={fill} />
                            ) : (
                              <XSvg color={fill} />
                            )}
                          </x.button>
                        );
                      })}
                    </x.div>
                    <x.button
                      type="button"
                      w="38px"
                      h="38px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      backgroundColor="#F8F1FF"
                      style={{
                        border: 'none',
                        padding: 0,
                        margin: 0,
                        cursor: contentEditable ? 'pointer' : 'not-allowed',
                        borderRadius: '8px'
                      }}
                      disabled={!contentEditable}
                      aria-label={t`Delete card`}
                      onClick={() => handleMarkDelete(id)}
                    >
                      <TrashIcon width={38} height={38} aria-hidden={true} />
                    </x.button>
                  </x.div>
                ))}
            </ScrollArea>
            {/* Add Card section (hidden when max reached) */}
            {(() => {
              const existingVisible = items.filter(
                i => !pendingDelete.has(i.id)
              );
              const newVisible = pendingNew.filter(
                id => !pendingDelete.has(id)
              );
              const totalVisible = existingVisible.length + newVisible.length;
              if (totalVisible >= MAX_CARDS) return null;
              return (
                <x.div
                  mt="13px"
                  display="flex"
                  justifyContent="flex-start"
                  flex="0 0 auto"
                >
                  <x.button
                    type="button"
                    w="87px"
                    h="38px"
                    backgroundColor="#F8F1FF"
                    color="#9738FF"
                    fontSize="14px"
                    fontWeight={700}
                    border="none"
                    borderRadius="6px 12px 6px 12px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    cursor={contentEditable ? 'pointer' : 'not-allowed'}
                    disabled={!contentEditable}
                    aria-label={t`Add Card`}
                    onClick={handleAddCard}
                  >
                    {t`Add Card`}
                  </x.button>
                </x.div>
              );
            })()}
            <x.div
              mt="18px"
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap="8px"
              flex="0 0 auto"
            >
              {submitError ? (
                <x.div
                  color="#C0223B"
                  fontSize="13px"
                  textAlign="center"
                  maxWidth="320px"
                >
                  {submitError}
                </x.div>
              ) : null}
              <x.button
                w="225px"
                h="38px"
                backgroundColor="#9738FF"
                color="white"
                fontSize="16px"
                fontWeight={600}
                border="none"
                borderRadius="8px"
                cursor={
                  !contentEditable || submitting ? 'not-allowed' : 'pointer'
                }
                opacity={submitting ? 0.7 : 1}
                disabled={!contentEditable || submitting}
                aria-label={t`Submit cards`}
                aria-busy={submitting}
                onClick={handleSubmit}
              >
                {submitting ? t`Sending…` : t`Submit`}
              </x.button>
            </x.div>
          </>
        )}
      </x.div>
    </AddCardsOverlay>
  );
};

// Overlay component that only triggers close if both mousedown & mouseup happen on the overlay itself.
const AddCardsOverlay: React.FC<{
  onRequestClose: () => void;
  children: React.ReactNode;
}> = ({ onRequestClose, children }) => {
  const downOriginRef = useRef<'overlay' | 'modal' | null>(null);

  // If mouse leaves window and comes back, reset origin to avoid accidental close
  useEffect(() => {
    const handleWindowMouseUp = () => {
      downOriginRef.current = null;
    };
    window.addEventListener('mouseup', handleWindowMouseUp);
    return () => window.removeEventListener('mouseup', handleWindowMouseUp);
  }, []);

  return (
    <x.div
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      display="flex"
      alignItems="center"
      justifyContent="center"
      zIndex={1000}
      backgroundColor="rgba(0,0,0,0.4)"
      role="dialog"
      aria-modal="true"
      aria-label={t`Add cards modal`}
      onMouseDown={e => {
        if (e.target === e.currentTarget) {
          downOriginRef.current = 'overlay';
        }
      }}
      onMouseUp={e => {
        const shouldClose =
          e.target === e.currentTarget && downOriginRef.current === 'overlay';
        downOriginRef.current = null;
        if (shouldClose) onRequestClose();
      }}
    >
      <div
        onMouseDown={() => {
          downOriginRef.current = 'modal';
        }}
        onClick={e => e.stopPropagation()}
        style={{ display: 'contents' }}
      >
        {children}
      </div>
    </x.div>
  );
};

const ScrollArea = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;

  /* Don't let flex shrink rows — otherwise multi-line text overflows
     onto the next card instead of growing the row / scrolling. */
  > * {
    flex-shrink: 0;
  }
`;
